// data.ts - the dx5 Diagnose grade payload, transcribed 1:1 from the locked mockup
// (docs/ui-explorations/local-mode-read-first-v2.html, the DSTREETS[].grade objects). Authored teaching
// content, sanctioned by PRODUCT_NORTH_STAR §5 (same sanction as diagnose/data.ts and the felt cards).
//
// Per street: the correct-line grade (`right`, always move class `best` for the hand axis) with its coach
// callout + deep debrief, plus the `wrong` branches keyed by the diverging action (move class + coaching
// that fires ONLY when the user picks that action). River has no wrong.fold (fold is correct) and no
// raise branch (river is fold/call only) - matching diagnose/data.ts river actions.
import type { MoveClass, DebriefContent } from './types';

/** The correct-line grade for one street: the hand-axis label + coaching, the good-path callout, and the
 *  deep debrief. */
export interface RightGrade {
  /** the action label shown after "Best ." on the hand axis ("Raise to isolate", "Call", "Fold"). */
  handLabel: string;
  /** the hand-axis coaching paragraph. */
  handText: string;
  /** the good-path coach callout (its bold lead is always "Carry this forward."). */
  coach: string;
  /** the deep-debrief payload for the drawer. */
  debrief: DebriefContent;
}

/** One wrong-answer branch: the move class it grades to + the labels/coaching for diverging this way. */
export interface WrongGrade {
  cls: MoveClass;
  handLabel: string;
  handText: string;
  coach: string;
}

/** The full authored grade payload for one street. */
export interface StreetGrade {
  right: RightGrade;
  /** wrong branches keyed by the action the user picked instead of `correct`. */
  wrong: Partial<Record<'fold' | 'call' | 'raise', WrongGrade>>;
  /** the correct action for this street (drives right-vs-wrong resolution). */
  correct: 'fold' | 'call' | 'raise';
  /** a correct raise typed outside [lo,hi] downgrades best -> exc + appends a sizing note. Preflop only. */
  raiseWindow?: { lo: number; hi: number };
}

export const DIAGNOSE_GRADES: StreetGrade[] = [
  // ---------- preflop: correct = raise to isolate ----------
  {
    correct: 'raise',
    raiseWindow: { lo: 15, hi: 21 },
    right: {
      handLabel: 'Raise to isolate',
      handText:
        'Raising folds the blinds and leaves you heads up and in position against the one player you want. A-J suited is far too strong to limp behind or fold.',
      coach:
        'On a loose, passive table you make money by isolating the weak seats in position, not by seeing cheap multiway flops. Size up so the field folds.',
      debrief: {
        title: 'Isolating the limper',
        body:
          'A-J suited plays great heads up and in position, and badly in a five-way limped pot where top pair is hard to trust. Raising to about 6 big blinds folds the blinds and the field, leaving you one on one with a player who calls too much.',
        example: {
          label: 'Sizing:',
          text: '$15 to $21 is the window. Anything near a min-raise just builds a family pot, the opposite of what you want.',
        },
      },
    },
    wrong: {
      fold: {
        cls: 'blun',
        handLabel: 'Fold',
        handText:
          'Folding A-J suited to a single limp throws away one of your best isolation hands. Far too tight.',
        coach:
          'A-J suited is a clear raise against a limper. Folding here leaves money on the table every orbit.',
      },
      call: {
        cls: 'inacc',
        handLabel: 'Just call',
        handText:
          'Calling lets the blinds come along cheap and turns a clean heads-up edge into a muddy multiway pot where your top pairs are hard to play.',
        coach:
          'Against one limper, raise to isolate. Flatting invites the field and wastes your position.',
      },
    },
  },
  // ---------- flop: correct = call ----------
  {
    correct: 'call',
    right: {
      handLabel: 'Call',
      handText:
        'Calling keeps every worse hand in: his weaker jacks, his medium pairs, his draws. Raising folds all of those out and keeps only what already beats you.',
      coach:
        'One out-of-character bet is a Level 1 signal, a question, not proof. You answered it by staying wide and taking the cheap value. If he fires again, that becomes a pattern.',
      debrief: {
        title: 'Why flat, not raise',
        body:
          'On a dry J 8 4 a Vacuum leads mostly pairs and jacks, rarely a bluff and rarely the nuts. Your top pair top kicker beats most of that. Calling gets value from the worse hands and keeps his few bluffs in. Raising only isolates you against the hands that beat you.',
        example: {
          label: 'Read:',
          text: 'weight him toward the middle, medium pairs and weak jacks, not the nuts and not air.',
        },
      },
    },
    wrong: {
      fold: {
        cls: 'mist',
        handLabel: 'Fold',
        handText:
          'Folding top pair top kicker to the Vacuum leading for the first time all night is far too weak. One odd bet is a question, not a hand you fold a strong pair to.',
        coach: 'Do not fold a strong hand to a single Level 1 signal. Call and keep watching.',
      },
      raise: {
        cls: 'mist',
        handLabel: 'Raise',
        handText:
          'Raising folds out every worse hand that would have paid you and keeps in only the two pair and sets that beat you. You turn a value hand into a bluff-catcher facing a raise.',
        coach:
          'Against a station, raise for value only when worse hands call. Here they fold and better hands stay. Just call.',
      },
    },
  },
  // ---------- turn: correct = call ----------
  {
    correct: 'call',
    right: {
      handLabel: 'Call',
      handText:
        'Enough worse jacks and stubborn pairs still bet twice to make one more call right. But the read has moved up, so you decide your river plan now.',
      coach:
        'Two barrels from a Vacuum is Level 2, a pattern. You call once more but commit to a plan: if a blank river brings a third big bet, you are done. Deciding in advance is the whole skill.',
      debrief: {
        title: 'Call, but plan ahead',
        body:
          'A blank turn that improves nothing, met with a second barrel, tells you he likes his hand. His range shifts toward value. Top pair is still ahead of enough weak jacks to call one more reasonable bet, but you are planning for the river, not hoping.',
        example: {
          label: 'The plan:',
          text: 'if the river is a blank and he bets big again, that is Level 3 and you fold. Say it now, before the card comes.',
        },
      },
    },
    wrong: {
      fold: {
        cls: 'inacc',
        handLabel: 'Fold',
        handText:
          'Folding here is a touch too soon. At Level 2 he still shows up with enough worse jacks and pairs that one more small call is right. The disciplined fold comes on the river.',
        coach:
          'Do not fold to the second barrel yet. Call once more with a stated river plan, then trust it.',
      },
      raise: {
        cls: 'mist',
        handLabel: 'Raise',
        handText:
          'Raising a bluff-catcher makes no sense: worse hands fold, better hands continue, and you have turned top pair into a bluff. You are not ahead of enough of his two-barrel range to raise for value.',
        coach: 'You hold a bluff-catcher, not a raising hand. Call and plan the river.',
      },
    },
  },
  // ---------- river: correct = fold (all-in, fold/call only) ----------
  {
    correct: 'fold',
    right: {
      handLabel: 'Fold',
      handText:
        'You ask the only question that matters: what worse hand bets like this? Against a Vacuum, almost none. He is not turning a made hand into a triple-barrel bluff. Top pair is now a clear bluff-catcher that mostly loses.',
      coach:
        'Folding a pretty hand is cheaper than paying to find out he finally has it. Same top pair the whole way, three different correct actions, because the read kept changing.',
      debrief: {
        title: 'The read beats the hand',
        body:
          'Three barrels from a lifelong caller on a board where every draw bricked is as close to a value announcement as poker gives you. You need 28% to call and you are nowhere near it. A bluff-catcher is only a call when the player actually bluffs, and this one does not.',
        example: {
          label: 'The whole hand:',
          text: 'Level 1 you stayed wide, Level 2 you called and planned, Level 3 you folded. The cards never changed. The reading did.',
        },
      },
    },
    wrong: {
      call: {
        cls: 'blun',
        handLabel: 'Call',
        handText:
          'You paid off a third barrel from a lifelong caller on a bricked board. He almost never has a bluff here, so this call loses far more often than the 28% you need. This is the most expensive mistake against a Vacuum.',
        coach:
          'When a player who never bluffs fires three big bets and every draw missed, believe him. A bluff-catcher against a station with no bluffs is a fold.',
      },
    },
  },
];

/** The read axis (B) is scored on the tag, the same across streets (mockup commitAction lines 1082-1087):
 *  tag the villain a Vacuum and the read is right. */
export const READ_AXIS = {
  right: {
    cls: 'exc' as MoveClass,
    label: 'Read the room right',
    text: 'You tagged him a Vacuum, a player who calls too much and almost never bluffs. That is the read the whole hand turns on.',
  },
  wrong: {
    cls: 'inacc' as MoveClass,
    label: 'Read a little off',
    // {tag} is filled with what the user actually tagged (or "nothing yet").
    text: 'You tagged him {tag}. His limp-then-fire line is a classic Vacuum: a lifelong caller who only bets when he likes it. Tag that and the decisions get easy.',
  },
};
